
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, MessageSquare, Store } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface StatsProps {
  memberStats: {
    total: number;
    pending: number;
    byState: {state: string, count: number}[];
  };
  messageCount: number;
  businessCount: number;
  loading: boolean;
}

export const DashboardStats = ({ 
  memberStats, 
  messageCount, 
  businessCount,
  loading 
}: StatsProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-4 w-4" /> Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{memberStats.total}</div>
          <div className="text-sm text-gray-500">
            {memberStats.pending} pending approval
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Announcements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{messageCount}</div>
          <div className="text-sm text-gray-500">
            Total announcements posted
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Store className="h-4 w-4" /> Businesses
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{businessCount}</div>
          <div className="text-sm text-gray-500">
            Approved marketplace listings
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const MembershipByState = ({ 
  memberStats,
  loading
}: {
  memberStats: {
    total: number;
    pending: number;
    byState: {state: string, count: number}[];
  };
  loading: boolean;
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Membership by State</CardTitle>
        <CardContent>Distribution of members across states</CardContent>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4">Loading statistics...</div>
        ) : (
          <div className="space-y-2">
            {memberStats.byState.map((item) => (
              <div key={item.state} className="flex justify-between items-center">
                <span>{item.state}</span>
                <div className="flex items-center gap-2">
                  <div className="bg-gray-200 w-32 h-2 rounded-full overflow-hidden">
                    <div 
                      className="bg-omoduduwa-terracotta h-full"
                      style={{ width: `${(item.count / memberStats.total) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium">{item.count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
